/**
 * Uploads a file to a presigned POST target (S3 or local-upload).
 *
 * Local and S3 document endpoints return `{ url, fields }`. The file must be
 * appended last as `file` so the local upload handler and S3 accept the body.
 */

export interface PresignedPostTarget {
  url: string;
  fields: Record<string, string>;
}

/**
 * True when the value looks like a presigned POST payload.
 *
 * @param value - Raw upload_url from the API
 * @returns Whether the value is a `{ url, fields }` object
 */
export function isPresignedPostTarget(value: unknown): value is PresignedPostTarget {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as PresignedPostTarget;
  return typeof candidate.url === 'string' && !!candidate.fields && typeof candidate.fields === 'object';
}

/**
 * POSTs the file to a presigned upload target using multipart form data.
 *
 * @param target - Presigned POST url + form fields
 * @param file - Browser File to upload
 */
export async function uploadToPresignedPost(
  target: PresignedPostTarget,
  file: File,
): Promise<void> {
  const form = new FormData();
  Object.entries(target.fields).forEach(([key, value]) => {
    form.append(key, value);
  });
  form.append('file', file);
  const response = await fetch(target.url, { method: 'POST', body: form });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
}

/**
 * POSTs the file to a presigned upload target, reporting progress and
 * honouring cancellation.
 *
 * `fetch` has no upload-progress event, so this uses XMLHttpRequest instead
 * -- needed for R16's "progress in percent, with the option to cancel".
 *
 * @param target - Presigned POST url + form fields.
 * @param file - Browser File to upload.
 * @param onProgress - Called with 0-100 as the upload advances.
 * @param signal - Aborts the upload when triggered.
 */
export function uploadToPresignedPostWithProgress(
  target: PresignedPostTarget,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(target.fields).forEach(([key, value]) => form.append(key, value));
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', target.url);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(form);
  });
}
